import { useState, useEffect } from "react"
import { Place, Trail } from "../../../types/global"
import { useDb } from "../../../contexts/DbContext"
import strToUrl from "../../../helpers/strToUrl"
import { type TrailPlace } from "../../../types/global"
import Button from "../../../components/Button"
import {
  TextField,
  MenuItem,
  FormHelperText,
  InputAdornment,
} from "@material-ui/core"
import { Errors } from "../index"
import * as S from "../styles"

interface TrailAssignmentProps {
  currentPlace: Place
  setCurrentPlace: React.Dispatch<React.SetStateAction<Place>>
  trailsAssigned: number
  setTrailsAssigned: Function
  setError: Function
  validationErrors: Errors | null
}

export default function TrailAssignment({
  currentPlace,
  setCurrentPlace,
  trailsAssigned,
  setTrailsAssigned,
  setError,
  validationErrors,
}: TrailAssignmentProps) {
  const { getTrailsPlaces, getTrails } = useDb()
  const [trailsPlaces, setTrailsPlaces] = useState<TrailPlace[][]>([])
  const [trails, setTrails] = useState<Trail[]>([])

  let assignmets = []

  // Get all trails from database
  useEffect(() => {
    getTrails()
      .then((querySnapshot: any) => {
        const newTrails: Trail[] = []
        querySnapshot.forEach((doc: any) => {
          newTrails.push(doc.data())
        })
        setTrails(newTrails)
      })
      .catch((error: any) => {
        setError("Error getting trails")
      })
    return () => {
      setTrails([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update (mark) place list for all place trails
  useEffect(() => {
    if (currentPlace.trails) {
      for (let id = 0; id < currentPlace.trails.length; id++) {
        updateTrailPlaces(id, currentPlace.trails[id])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlace.trails])

  // Update place list for trail(s) selects
  const updateTrailPlaces = (id: number, trailName: string) => {
    const trailsPlacesJson = getTrailsPlaces(trailName)
    Object.entries(trailsPlacesJson).forEach(([name, places]: any) => {
      if (strToUrl(name) === trailName) {
        const newTrailsPlaces = trailsPlaces ?? []
        newTrailsPlaces[id] = places
        setTrailsPlaces(newTrailsPlaces)
      }
    })
  }

  // Remove last assigned trail
  function removeAssignment(id: number) {
    if (currentPlace?.trails?.[id]) {
        const newTrails = currentPlace.trails
        newTrails.splice(id, 1)
        setCurrentPlace({ ...currentPlace, trails: newTrails })
      }

    if (trailsPlaces[id]) {
      const newTrailPlaces = trailsPlaces
      newTrailPlaces.splice(id, 1)
      setTrailsPlaces(trailsPlaces)
    }

    if (currentPlace.distances?.[id]) {
      const newDistances = currentPlace.distances
      newDistances.splice(id, 1)
      setCurrentPlace({ ...currentPlace, distances: newDistances })
    }

    if (currentPlace.trailsDistances?.[id]) {
      const newTrailsDistances = currentPlace.trailsDistances
      newTrailsDistances.splice(id, 1)
      setCurrentPlace({ ...currentPlace, trailsDistances: newTrailsDistances })
    }

    setTrailsAssigned(trailsAssigned - 1)
  }

  const handlePlaceTrailChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: number,
  ) => {
    let newTrails = currentPlace.trails ?? []
    newTrails[id] = event.target.value

    if (!currentPlace || event.target.value === undefined) return
    setCurrentPlace((prevState) => ({
      ...prevState,
      trails: newTrails,
    }))

    updateTrailPlaces(id, event.target.value)
  }

  const handlePlaceTrailDistanceChange = (id: any, distance: any) => {
    let newTrailsDistances = currentPlace.trailsDistances ?? []
    newTrailsDistances[id] = distance

    setCurrentPlace((prevState) => ({
      ...prevState,
      trailsDistances: newTrailsDistances,
    }))
  }

  const handleDistanceChange = (id: any, distance: any) => {
    let newDistances = currentPlace.distances ?? []
    newDistances[id] = parseFloat(distance)
    setCurrentPlace({ ...currentPlace, distances: newDistances })
  }

  for (let id = 0; id < trailsAssigned; id++) {
    assignmets.push(
      <div key={id}>
        {id === trailsAssigned - 1 && id !== 0 && (
          <S.Right>
            <Button
              content="Odebrat tuto trasu"
              onClick={() => removeAssignment(id)}
              color="gray"
              size="small"
            />
          </S.Right>
        )}
        <S.TextFieldWrapper>
          <TextField
            label="Trasa"
            select
            value={
              currentPlace.trails
                ? trails.some(
                    (trail) =>
                      strToUrl(trail.name) === currentPlace?.trails?.[id],
                  )
                  ? currentPlace.trails[id] || ""
                  : ""
                : ""
            }
            onChange={(e) => handlePlaceTrailChange(e, id)}
            required
            style={{ width: 230 }}
            variant="outlined"
            error={validationErrors?.trails?.[id] !== undefined}
            helperText={
              validationErrors?.trails?.[id] && validationErrors.trails[id]
            }
          >
            {trails &&
              trails.map((trail, index) => {
                return (
                  <MenuItem
                    key={strToUrl(trail.name) + index}
                    value={strToUrl(trail.name)}
                    disabled={currentPlace.trails?.includes(
                      strToUrl(trail.name),
                    )}
                  >
                    {trail.name}
                  </MenuItem>
                )
              })}
          </TextField>
        </S.TextFieldWrapper>
        {currentPlace.trails !== undefined &&
          trailsPlaces &&
          trailsPlaces[id] && (
            <S.TextFieldWrapper>
              <TextField
                style={{ maxWidth: "calc(100vw - 40px)", minWidth: "300px" }}
                label="Místo na trase"
                select
                value={
                  currentPlace.trailsDistances
                    ? trailsPlaces[id].find((trailPlace) =>
                        trailPlace.km === currentPlace?.trailsDistances?.[id]
                          ? currentPlace.trailsDistances[id] || ""
                          : "",
                      )?.km || 0
                    : ""
                }
                onChange={(e) =>
                  handlePlaceTrailDistanceChange(id, e.target.value)
                }
                required
                variant="outlined"
                error={validationErrors?.trailsDistances?.[id] !== undefined}
                helperText={
                  validationErrors?.trailsDistances?.[id] &&
                  validationErrors.trailsDistances[id]
                }
              >
                {trailsPlaces[id].map((trailPlace, index) => {
                  return (
                    <MenuItem
                      key={strToUrl(trailPlace.name) + index}
                      value={trailPlace.km}
                    >
                      {trailPlace.km + " km - " + trailPlace.name}
                      {trailsPlaces[id].length > index + 1 &&
                        " ↔ " +
                          trailsPlaces[id][index + 1].km +
                          " km - " +
                          trailsPlaces[id][index + 1].name}
                    </MenuItem>
                  )
                })}
              </TextField>
              <FormHelperText id="details-helper-text">
                Ubytování se nachází mezi těmito dvěma body
              </FormHelperText>
            </S.TextFieldWrapper>
          )}
        <S.TextFieldWrapper>
          <TextField
            style={{ width: 300 }}
            type="number"
            label="Vzdálenost od trasy"
            name="distance"
            value={
              currentPlace.distances
                ? isNaN(currentPlace.distances[id])
                  ? ""
                  : currentPlace.distances[id]
                : ""
            }
            onChange={(e) => handleDistanceChange(id, e.target.value)}
            variant="outlined"
            required
            inputProps={{
              step: "0.1",
              lang: "en-US",
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">km</InputAdornment>,
            }}
            error={validationErrors?.distances?.[id] !== undefined}
            helperText={
              validationErrors?.distances?.[id] &&
              validationErrors.distances[id]
            }
          />
          <FormHelperText id="details-helper-text">
            Vzdálenost od nejbližšího bodu k tobě
          </FormHelperText>
        </S.TextFieldWrapper>
      </div>,
    )
  }

  return <>{assignmets}</>
}
